import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { CONTACT, SINCE, breadcrumb, faqSchema, SITE } from "./marketingData";
import { CITIES, verticalByKey } from "./seoContent";

export default function CityIndex({ verticalKey }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const v = verticalByKey(verticalKey);
  if (!v) return <Navigate to="/" replace />;

  const INDEX_FAQ = [
    { q: `Which cities does ITO cover for ${v.vehicle} transport?`, a: `Indian Transport Organisation runs ${v.vehicle} movements to and from every major industrial hub in India — including ${CITIES.slice(0, 8).map((c) => c.name).join(", ")} and beyond — with delivery to any pincode.` },
    { q: `Why use ITO for ${v.vehicle} transport?`, a: v.cargoAnswer },
    { q: "Do you handle over-dimensional loads in every city?", a: "Yes — we arrange route surveys, state permits, RTO clearances and pilot escorts for over-dimensional movements from every city we serve." },
  ];

  const jsonLd = [
    breadcrumb([{ name: "Home", path: "/" }, { name: v.noun, path: v.base }]),
    {
      "@context": "https://schema.org", "@type": "ItemList",
      itemListElement: CITIES.map((c, i) => ({
        "@type": "ListItem", position: i + 1, name: `${v.vehicle} transport in ${c.name}`,
        url: `${SITE}${v.base}/${c.slug}`,
      })),
    },
    faqSchema(INDEX_FAQ),
  ];

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid={`city-index-${v.key}`}>
      <Seo
        title={`${v.noun} in India — City Network | ITO (since ${SINCE})`}
        description={`${v.vehicle} transport across every major Indian industrial hub — Mumbai, Pune, Delhi NCR, Chennai, Bengaluru, Ahmedabad, Hyderabad and more. Moved safely to any pincode.`}
        path={v.base}
        image={v.image}
        imageAlt={`${v.noun} network across India`}
        keywords={`${v.noun.toLowerCase()} india, ${v.vehicle} transport, ${v.noun.toLowerCase()} near me`}
        jsonLd={jsonLd}
      />
      <MarketingNav heroDark />

      <section className="relative min-h-[46vh] flex items-end pb-12 pt-32 overflow-hidden">
        <img src={v.image} alt={`${v.vehicle} transport across India`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/93 via-[#1A1A18]/60 to-[#1A1A18]/30" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full">
          <h1 className="font-heading font-black tracking-tight text-white text-3xl sm:text-5xl leading-[1.05] max-w-3xl">
            {v.noun} in India
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl leading-relaxed">
            {v.lead} across every major industrial hub — to any pincode, since {SINCE}.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-base text-[#4F4C44] leading-relaxed max-w-3xl">
              Pick your city to see local coverage, cargo types and popular routes. With 800+ owned vehicles,
              ITO matches the right vehicle to your cargo and delivers safely from pickup to destination.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CITIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                <Link to={`${v.base}/${c.slug}`} data-testid={`city-card-${c.slug}`}
                  className="group block h-full rounded-2xl border border-[#1A1A18]/10 bg-white p-6 hover:border-[#C84B31]/50 hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="flex items-center gap-2.5 mb-2">
                    <MapPin className="w-4 h-4 text-[#C84B31]" />
                    <h2 className="font-heading font-semibold text-lg">{c.name}</h2>
                  </div>
                  <p className="text-sm text-[#4F4C44] leading-relaxed mb-4">{c.state} · {c.areas}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                    View {c.name} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <div className="rounded-2xl bg-[#1A1A18] text-white p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <h2 className="font-heading font-bold text-2xl mb-1">Not sure which vehicle you need?</h2>
                <p className="text-white/80 text-sm">Read our guide or talk to a coordinator.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/guides/${v.guideSlug}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                  Read the guide <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                  <Phone className="w-4 h-4" /> Call {CONTACT.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={INDEX_FAQ} title="City network — frequently asked questions" testid="city-index-faq" />

      <MarketingFooter />
    </div>
  );
}
